import React, { useState, useRef } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';

const EditProfile = ({ profile, supabase, onSuccess, onCancel }) => {
  const [username, setUsername] = useState(profile.username || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageUrl, setImageUrl] = useState(profile.avatar_url || null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const getInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : 'U';
  };

  const handleFileChange = async (e) => {
    try {
      if (!e.target.files || e.target.files.length === 0) {
        return;
      }
      
      const file = e.target.files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${profile.id}.${fileExt}`;
      const filePath = `avatars/${fileName}`;
      
      setUploading(true);
      setError(null);
      
      // Get the current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('Auth error:', sessionError);
        throw new Error('Authentication required');
      }
      
      console.log('Using auth token for user:', session.user.id);
      
      // Use the fetch API directly with authentication headers
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const storageUrl = `${supabaseUrl}/storage/v1/object/profiles/${filePath}`;
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(storageUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Upload failed:', errorData);
        throw new Error(errorData.message || 'Upload failed');
      }
      
      // Get public URL
      const { data: publicURL } = supabase.storage
        .from('profiles')
        .getPublicUrl(filePath);
      
      console.log('Upload successful, URL:', publicURL.publicUrl);
      setImageUrl(publicURL.publicUrl);
    } catch (err) {
      console.error('Error uploading avatar:', err.message);
      setError('Error uploading image: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username.trim()) {
      setError('Username cannot be empty');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Update the player record
      const { data, error: updateError } = await supabase
        .from('players')
        .update({ 
          username: username.trim(),
          avatar_url: imageUrl 
        })
        .eq('id', profile.id)
        .select()
        .single();
      
      if (updateError) throw updateError;
      
      // Call the success callback with the updated profile
      onSuccess(data);
      
    } catch (err) {
      console.error('Error updating profile:', err.message);
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Edit Profile</CardTitle>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col items-center mb-6">
            <Avatar 
              className="h-24 w-24 cursor-pointer ring-2 ring-primary ring-offset-2 ring-offset-background"
              onClick={triggerFileInput}
            >
              {imageUrl ? (
                <AvatarImage src={imageUrl} alt={username} />
              ) : (
                <AvatarFallback className="text-2xl bg-primary/20 text-primary">
                  {getInitial(username)}
                </AvatarFallback>
              )}
            </Avatar>
            
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            
            <Button 
              type="button"
              variant="outline"
              className="mt-3 text-xs"
              size="sm"
              onClick={triggerFileInput}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : imageUrl ? 'Change Photo' : 'Upload Photo'}
            </Button>
          </div>
          
          <div className="space-y-2">
            <label 
              htmlFor="username" 
              className="text-sm font-medium"
            >
              Username
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              className="w-full p-2 border rounded-md bg-background"
              required
            />
          </div>
          
          {error && (
            <div className="p-3 rounded-md bg-destructive/10 text-destructive">
              {error}
            </div>
          )}
        </form>
      </CardContent>
      
      <CardFooter className="flex justify-between">
        <Button 
          type="button" 
          variant="outline"
          onClick={onCancel}
          disabled={loading || uploading}
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          onClick={handleSubmit}
          disabled={loading || uploading}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default EditProfile; 